import { MouseEvent, useCallback, useMemo, useRef } from 'react';
import {
  MOUSE_MOVE_EVENT_THROTTLE,
  Margin,
  Size,
  getTarget,
  panAndZoom,
} from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import { isEqual, throttle } from 'lodash';
import { BubbleChartProperties, HighlightState } from './types';
import { EMPTY_HIGHLIGHT_STATE, ZERO_BUBBLE, ZERO_BUBBLE_SIZE } from './consts';
import { BubbleChartContainer, BubbleChartSvgElement } from './atoms';
import { MAIN_GROUP_CONTAINER_CLASSNAME } from '@ardoq/global-consts';
import { useResizeObserver } from '@ardoq/hooks';
import { XAxis } from './xAxis';
import { YAxis } from './yAxis';
import { ChartArea } from './chartArea';
import { QuadrantOverlay } from './quadrantOverlay';

const offset = (
  clientX: number,
  clientY: number,
  bbox: DOMRect,
  margin: Margin
): Point => [
  clientX - bbox.left - margin.left,
  clientY - bbox.top - margin.top,
];
const getHighlightState = (target: SVGElement | null): HighlightState =>
  target?.dataset
    ? {
        target: target,
        id: target.dataset.uniqueId ?? '',
        labelPosition: target.matches('.dataPointLabel')
          ? [
              parseFloat(target.getAttribute('x') || 'NaN'),
              parseFloat(target.getAttribute('y') || 'NaN'),
            ]
          : null,
      }
    : { ...EMPTY_HIGHLIGHT_STATE, target: target };

const getBubbleUniqueId = (e: MouseEvent): string | null => {
  const target = getTarget(e.nativeEvent.target);
  if (!(target instanceof SVGElement)) {
    return null;
  }
  return target.dataset.uniqueId || null;
};

const BubbleChartBase = ({
  viewInstanceId,
  selectComponent,
  isPanning,
  registerPanZoomClient,
  hoverState,
  focusState,
  clientRect,
  dimensions,
  chartRenderingParameters,
  bubbleChartCommands,
}: BubbleChartProperties) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const handleViewContainerResize = useCallback(
    ({ height = 0, width = 0 }: Size) => {
      if (clientRect.width === width && clientRect.height === height) {
        return;
      }
      bubbleChartCommands.setClientRect({ width, height });
    },
    [clientRect.height, clientRect.width, bubbleChartCommands]
  );

  useResizeObserver(containerRef, handleViewContainerResize);

  const hoverBubble = useMemo(
    () =>
      throttle((e: MouseEvent) => {
        if (isPanning()) {
          return;
        }
        const target = getTarget(e.nativeEvent.target);
        const newHighlightState =
          target instanceof SVGElement
            ? getHighlightState(target)
            : EMPTY_HIGHLIGHT_STATE;

        if (!isEqual(newHighlightState, hoverState)) {
          bubbleChartCommands.updateHoverState(
            newHighlightState,
            viewInstanceId
          );
        }
      }, MOUSE_MOVE_EVENT_THROTTLE),
    [isPanning, hoverState, viewInstanceId, bubbleChartCommands]
  );
  const focusBubble = useCallback(
    (e: MouseEvent) => {
      if (isPanning()) {
        return;
      }
      const target = getTarget(e.nativeEvent.target);
      const newHighlightState =
        target instanceof SVGElement
          ? getHighlightState(target)
          : EMPTY_HIGHLIGHT_STATE;

      if (!isEqual(newHighlightState, focusState)) {
        bubbleChartCommands.updateFocusState(newHighlightState, viewInstanceId);
      }
    },
    [isPanning, focusState, viewInstanceId, bubbleChartCommands]
  );

  const handleBubbleClick = useCallback(
    (e: MouseEvent) => {
      focusBubble(e);
      const uniqueId = getBubbleUniqueId(e);
      if (uniqueId) {
        bubbleChartCommands.onBubbleClick(uniqueId);
      }
    },
    [focusBubble, bubbleChartCommands]
  );

  const handleBubbleDoubleClick = useCallback(
    (e: MouseEvent) => {
      const uniqueId = getBubbleUniqueId(e);
      if (uniqueId) {
        bubbleChartCommands.onBubbleDoubleClick(uniqueId);
      }
    },
    [bubbleChartCommands]
  );

  registerPanZoomClient({
    getDimensions: () => dimensions,
    offsetToAxes: (e: WheelEvent) => {
      const targetElement = e.currentTarget;
      if (!(targetElement instanceof Element)) {
        return [0, 0];
      }
      return offset(
        e.clientX,
        e.clientY,
        targetElement.getBoundingClientRect(),
        dimensions.margin
      );
    },
    onWindowChanged: () =>
      bubbleChartCommands.updateHoverState(
        EMPTY_HIGHLIGHT_STATE,
        viewInstanceId
      ),
    getMouseWheelElement: () => containerRef.current!,
    selectComponent: args => {
      bubbleChartCommands.updateHoverState(
        EMPTY_HIGHLIGHT_STATE,
        viewInstanceId
      );
      selectComponent?.(args);
    },
  });

  const {
    xAxisProps,
    yAxisProps,
    quadrantOverlayProps,
    chartAreaProps,
    chartAreaClipPathUrl,
  } = chartRenderingParameters;

  const containerClipPathUrl = `containerClipPath-${viewInstanceId}`;
  return (
    <BubbleChartContainer
      ref={containerRef}
      onContextMenu={e => e.preventDefault()}
    >
      <BubbleChartSvgElement
        height={dimensions.container.height}
        width={dimensions.container.width}
        onMouseMove={hoverBubble}
        onClick={handleBubbleClick}
        onDoubleClick={handleBubbleDoubleClick}
      >
        <defs>
          <symbol
            id={ZERO_BUBBLE}
            width={ZERO_BUBBLE_SIZE}
            height={ZERO_BUBBLE_SIZE}
            viewBox="0 0 6 6"
          >
            <path
              d="M4.99994 5L0.999972 1.00003"
              stroke="#6F7D90"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M1.00006 5L5.00003 1.00003"
              stroke="#6F7D90"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </symbol>
        </defs>
        <clipPath id={chartAreaClipPathUrl}>
          <rect
            height={Math.max(0, dimensions.chart.height)}
            width={Math.max(0, dimensions.chart.width)}
          />
        </clipPath>

        <g
          clipPath={`url(#${containerClipPathUrl})`}
          className={`${MAIN_GROUP_CONTAINER_CLASSNAME}`}
        >
          <clipPath id={containerClipPathUrl}>
            <rect
              height={dimensions.container.height}
              width={dimensions.container.width}
            />
          </clipPath>
          {quadrantOverlayProps && (
            <QuadrantOverlay {...quadrantOverlayProps} />
          )}
          <XAxis {...xAxisProps} />
          <YAxis {...yAxisProps} />
          <ChartArea {...chartAreaProps} />
        </g>
      </BubbleChartSvgElement>
    </BubbleChartContainer>
  );
};

export const BubbleChart = panAndZoom(BubbleChartBase);
